import { useEffect, useMemo, useState } from "react";
import PasswordField from "@/components/PasswordField";
import {
  adminCatalog,
  adminLogin,
  adminLogout,
  adminMe,
  adminSettings,
  clearEditCache,
  deleteFamily,
  deleteVariant,
  forgotPassword,
  formatUsd,
  saveAdminSettings,
  saveFamily,
  saveOpenAiKey,
  savePasswords,
  saveVariant,
  testRecoveryEmail,
  uploadVariantImage,
} from "@/lib/siteApi";

const serif = '"Cormorant Garamond", Georgia, serif';

function fieldClass() {
  return "mt-1 w-full rounded-xl border border-[#e4ddd4] bg-white px-3 py-2 text-sm";
}

function Section({ title, children }) {
  return (
    <section className="rounded-3xl border border-[#ece6de] bg-white p-5 sm:p-6">
      <h2 className="text-2xl text-[#1c1917]" style={{ fontFamily: serif }}>{title}</h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

function variantImage(variant) {
  if (!variant?.image) return "";
  return variant.imageKind === "upload" ? `/api/media/${variant.image}` : `/hair/${variant.image}`;
}

export default function Admin() {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("akses");
  const [settings, setSettings] = useState(null);
  const [catalog, setCatalog] = useState({ families: [] });
  const [busy, setBusy] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [passwords, setPasswordForm] = useState({ currentAdminPassword: "", adminPassword: "", visitorPassword: "" });
  const [familyForm, setFamilyForm] = useState({ id: "", name: "", gender: "wanita", summary: "" });
  const [variantForm, setVariantForm] = useState({
    familyId: "",
    id: "",
    name: "",
    detail: "",
    length: "medium",
    prompt: "",
  });

  const load = async () => {
    const [nextSettings, nextCatalog] = await Promise.all([adminSettings(), adminCatalog()]);
    setSettings(nextSettings);
    setCatalog(nextCatalog);
  };

  useEffect(() => {
    document.title = "MIJI Admin";
    adminMe()
      .then(async () => {
        setAuthed(true);
        await load();
      })
      .catch(() => setAuthed(false))
      .finally(() => setReady(true));
  }, []);

  const cfg = settings?.config;
  const tryOn = cfg?.tryOn || {};
  const price = useMemo(() => {
    const table = settings?.prices?.[tryOn.model] || settings?.prices?.["gpt-image-1-mini"] || {};
    return table?.[tryOn.quality]?.[tryOn.size];
  }, [settings, tryOn.model, tryOn.quality, tryOn.size]);

  const run = async (job, ok = "Tersimpan.") => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await job();
      setNotice(result?.message || ok);
    } catch (err) {
      setError(err.message || "Gagal menyimpan.");
    } finally {
      setBusy(false);
    }
  };

  const onLogin = (event) => {
    event.preventDefault();
    if (forgotOpen) {
      run(() => forgotPassword({ kind: "admin" }), "Password sent to: kli...c@g...com");
      return;
    }
    run(async () => {
      await adminLogin(password);
      setAuthed(true);
      setPassword("");
      await load();
    }, "Masuk admin.");
  };

  if (!ready) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f6f3ee] text-sm text-[#6d655e]">Memuat admin…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#f6f3ee] px-4 py-16 text-[#1c1917]" style={{ fontFamily: '"Manrope", Inter, sans-serif' }}>
        <form onSubmit={onLogin} className="mx-auto max-w-md rounded-3xl border border-[#ece6de] bg-white p-8">
          <div className="text-[11px] tracking-[0.22em] text-[#8a8178]">MIJI HAIRCARE</div>
          <h1 className="mt-2 text-4xl" style={{ fontFamily: serif }}>Admin</h1>
          <p className="mt-3 text-sm text-[#6d655e]">Login ini hanya untuk mengatur situs, bukan untuk pengunjung.</p>
          <div className="mt-6">
            <PasswordField label="Password admin" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
          </div>
          {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
          {notice && <p className="mt-3 text-sm text-emerald-800">{notice}</p>}
          <button type="submit" disabled={busy} className="mt-6 w-full rounded-full bg-[#1c1917] py-3 text-sm text-white disabled:opacity-50">
            Masuk
          </button>
          <button type="button" className="mt-3 w-full text-sm text-[#6d655e] underline" onClick={() => { setForgotOpen((v) => !v); setError(""); setNotice(""); }}>
            Lupa password?
          </button>
          {forgotOpen && (
            <div className="mt-4 space-y-3 border-t border-[#ece6de] pt-4">
              <p className="text-sm text-[#6d655e]">Password baru akan dikirim ke email terdaftar.</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => forgotPassword({ kind: "admin" }), "Password sent to: kli...c@g...com")}
                className="w-full rounded-full border border-[#d9d0c6] py-3 text-sm"
              >
                Kirim password
              </button>
            </div>
          )}
        </form>
      </div>
    );
  }

  const updateConfig = (patch) => {
    setSettings((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        ...patch,
        brand: { ...prev.config.brand, ...(patch.brand || {}) },
        tryOn: { ...prev.config.tryOn, ...(patch.tryOn || {}) },
        visitorGate: { ...prev.config.visitorGate, ...(patch.visitorGate || {}) },
      },
    }));
  };

  return (
    <div className="min-h-screen bg-[#f6f3ee] text-[#1c1917]" style={{ fontFamily: '"Manrope", Inter, sans-serif' }}>
      <header className="border-b border-[#ece6de] bg-[#f6f3ee]/95">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-4 sm:px-6">
          <div>
            <div className="text-[11px] tracking-[0.22em] text-[#8a8178]">MIJI</div>
            <h1 className="text-3xl" style={{ fontFamily: serif }}>Pengaturan Situs</h1>
          </div>
          <button
            type="button"
            className="ml-auto rounded-full border border-[#d9d0c6] bg-white px-4 py-2 text-sm"
            onClick={() => run(async () => { await adminLogout(); setAuthed(false); }, "Keluar.")}
          >
            Keluar
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="flex flex-wrap gap-2">
          {[["akses", "Akses"], ["openai", "OpenAI"], ["tryon", "Try-on"], ["katalog", "Katalog"], ["tampilan", "Tampilan"]].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`rounded-full px-4 py-2 text-sm ${tab === id ? "bg-[#1c1917] text-white" : "border border-[#e4ddd4] bg-white"}`}
            >
              {label}
            </button>
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-rose-700">{error}</p>}
        {notice && <p className="mt-4 text-sm text-emerald-800">{notice}</p>}

        <div className="mt-6 space-y-4">
          {tab === "akses" && cfg && (
            <>
              <Section title="Password pengunjung">
                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(cfg.visitorGate.enabled)}
                    onChange={(e) => updateConfig({ visitorGate: { enabled: e.target.checked } })}
                  />
                  Wajibkan kode akses sebelum analisa/try-on
                </label>
                <label className="block text-sm">
                  Judul layar kunci
                  <input value={cfg.visitorGate.title} onChange={(e) => updateConfig({ visitorGate: { title: e.target.value } })} className={fieldClass()} />
                </label>
                <label className="block text-sm">
                  Teks
                  <textarea value={cfg.visitorGate.text} onChange={(e) => updateConfig({ visitorGate: { text: e.target.value } })} className={fieldClass()} rows={3} />
                </label>
                <button type="button" disabled={busy} onClick={() => run(() => saveAdminSettings({ visitorGate: cfg.visitorGate }))} className="rounded-full bg-[#1c1917] px-5 py-2 text-sm text-white disabled:opacity-50">
                  Simpan kunci pengunjung
                </button>
              </Section>
              <Section title="Ganti password">
                {settings.defaults && <p className="text-sm text-[#8a5a3a]">Masih memakai password default. Ganti sekarang.</p>}
                <PasswordField label="Password admin saat ini" value={passwords.currentAdminPassword} onChange={(e) => setPasswordForm({ ...passwords, currentAdminPassword: e.target.value })} />
                <PasswordField label="Password admin baru" value={passwords.adminPassword} onChange={(e) => setPasswordForm({ ...passwords, adminPassword: e.target.value })} />
                <PasswordField label="Kode akses pengunjung baru" value={passwords.visitorPassword} onChange={(e) => setPasswordForm({ ...passwords, visitorPassword: e.target.value })} />
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(async () => {
                    await savePasswords(passwords);
                    setPasswordForm({ currentAdminPassword: "", adminPassword: "", visitorPassword: "" });
                  }, "Password diganti.")}
                  className="rounded-full bg-[#1c1917] px-5 py-2 text-sm text-white disabled:opacity-50"
                >
                  Simpan password
                </button>
              </Section>
              <Section title="Lupa password">
                <p className="text-sm text-[#6d655e]">
                  Password lama tidak bisa dikirim ulang. Tombol lupa password mengirim password baru ke {settings.recovery?.maskedEmail || "kli...c@g...com"}.
                </p>
                <button type="button" disabled={busy} onClick={() => run(() => testRecoveryEmail(), "Password sent to: kli...c@g...com")} className="rounded-full border border-[#d9d0c6] bg-white px-5 py-2 text-sm">
                  Kirim email tes
                </button>
              </Section>
            </>
          )}

          {tab === "openai" && settings && (
            <Section title="API key GPT">
              <p className="text-sm text-[#6d655e]">
                Key tidak ditampilkan utuh. {settings.openai.configured ? `Tersimpan: ${settings.openai.masked}` : "Belum ada key di panel ini; server masih memakai key dari environment jika ada."}
              </p>
              <label className="block text-sm">
                Key baru
                <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="sk-..." className={fieldClass()} />
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(async () => {
                  const result = await saveOpenAiKey(keyInput);
                  setKeyInput("");
                  setSettings((prev) => ({ ...prev, openai: result.openai }));
                }, "Key disimpan.")}
                className="rounded-full bg-[#1c1917] px-5 py-2 text-sm text-white disabled:opacity-50"
              >
                Simpan key
              </button>
            </Section>
          )}

          {tab === "tryon" && cfg && (
            <Section title="Kualitas, ukuran, dan harga">
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="block text-sm">
                  Model
                  <select value={tryOn.model} onChange={(e) => updateConfig({ tryOn: { model: e.target.value } })} className={fieldClass()}>
                    {(settings.models || []).map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </label>
                <label className="block text-sm">
                  Kualitas
                  <select value={tryOn.quality} onChange={(e) => updateConfig({ tryOn: { quality: e.target.value } })} className={fieldClass()}>
                    {(settings.qualities || []).map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </label>
                <label className="block text-sm">
                  Ukuran
                  <select value={tryOn.size} onChange={(e) => updateConfig({ tryOn: { size: e.target.value } })} className={fieldClass()}>
                    {(settings.sizes || []).map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </label>
              </div>
              <div className="rounded-2xl bg-[#f6f3ee] p-4 text-sm">
                <div>Perkiraan 1 gambar: <strong>{formatUsd(price)}</strong></div>
                <div>1 apply: {formatUsd(price)}</div>
                <div>4 apply: {formatUsd(typeof price === "number" ? price * 4 : price)}</div>
                <div>1 warna tambahan: {formatUsd(price)}</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="text-[#8a8178]">
                      <th className="py-2">Kualitas</th>
                      <th>1024×1024</th>
                      <th>1024×1536</th>
                      <th>1536×1024</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(settings.prices?.[tryOn.model] || {}).map(([quality, sizes]) => (
                      <tr key={quality} className="border-t border-[#ece6de]">
                        <td className="py-2">{quality}</td>
                        <td>{formatUsd(sizes["1024x1024"])}</td>
                        <td>{formatUsd(sizes["1024x1536"])}</td>
                        <td>{formatUsd(sizes["1536x1024"])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" checked={Boolean(tryOn.allowApplyAll)} onChange={(e) => updateConfig({ tryOn: { allowApplyAll: e.target.checked } })} />
                Tampilkan tombol “Aplikasikan semua 4”
              </label>
              <label className="block text-sm">
                Model analisa wajah
                <input value={tryOn.analysisModel} onChange={(e) => updateConfig({ tryOn: { analysisModel: e.target.value } })} className={fieldClass()} />
              </label>
              <div className="flex flex-wrap gap-3">
                <button type="button" disabled={busy} onClick={() => run(() => saveAdminSettings({ tryOn }))} className="rounded-full bg-[#1c1917] px-5 py-2 text-sm text-white disabled:opacity-50">
                  Simpan try-on
                </button>
                <button type="button" disabled={busy} onClick={() => run(() => clearEditCache(), "Cache try-on dikosongkan.")} className="rounded-full border border-[#d9d0c6] bg-white px-5 py-2 text-sm">
                  Kosongkan cache
                </button>
              </div>
            </Section>
          )}

          {tab === "katalog" && (
            <>
              <Section title="Tambah keluarga style">
                <div className="grid gap-3 sm:grid-cols-2">
                  <input placeholder="id, contoh women-bob" value={familyForm.id} onChange={(e) => setFamilyForm({ ...familyForm, id: e.target.value })} className={fieldClass()} />
                  <input placeholder="Nama" value={familyForm.name} onChange={(e) => setFamilyForm({ ...familyForm, name: e.target.value })} className={fieldClass()} />
                  <select value={familyForm.gender} onChange={(e) => setFamilyForm({ ...familyForm, gender: e.target.value })} className={fieldClass()}>
                    <option value="wanita">Wanita</option>
                    <option value="pria">Pria</option>
                  </select>
                  <input placeholder="Ringkasan" value={familyForm.summary} onChange={(e) => setFamilyForm({ ...familyForm, summary: e.target.value })} className={fieldClass()} />
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(async () => {
                    await saveFamily(familyForm);
                    setFamilyForm({ id: "", name: "", gender: familyForm.gender, summary: "" });
                    setCatalog(await adminCatalog());
                  }, "Keluarga ditambahkan.")}
                  className="rounded-full bg-[#1c1917] px-5 py-2 text-sm text-white disabled:opacity-50"
                >
                  Tambah keluarga
                </button>
              </Section>
              <Section title="Tambah / ubah varian">
                <div className="grid gap-3 sm:grid-cols-2">
                  <select value={variantForm.familyId} onChange={(e) => setVariantForm({ ...variantForm, familyId: e.target.value })} className={fieldClass()}>
                    <option value="">Pilih keluarga</option>
                    {catalog.families.map((family) => (
                      <option key={family.id} value={family.id}>{family.name}</option>
                    ))}
                  </select>
                  <input placeholder="id varian" value={variantForm.id} onChange={(e) => setVariantForm({ ...variantForm, id: e.target.value })} className={fieldClass()} />
                  <input placeholder="Nama tampilan" value={variantForm.name} onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })} className={fieldClass()} />
                  <select value={variantForm.length} onChange={(e) => setVariantForm({ ...variantForm, length: e.target.value })} className={fieldClass()}>
                    <option value="short">short</option>
                    <option value="bob">bob</option>
                    <option value="medium">medium</option>
                    <option value="long">long</option>
                  </select>
                  <textarea placeholder="Deskripsi singkat" value={variantForm.detail} onChange={(e) => setVariantForm({ ...variantForm, detail: e.target.value })} className={`${fieldClass()} sm:col-span-2`} rows={2} />
                  <textarea placeholder="Prompt try-on (bahasa Inggris)" value={variantForm.prompt} onChange={(e) => setVariantForm({ ...variantForm, prompt: e.target.value })} className={`${fieldClass()} sm:col-span-2`} rows={3} />
                </div>
                <button
                  type="button"
                  disabled={busy || !variantForm.familyId}
                  onClick={() => run(async () => {
                    await saveVariant(variantForm);
                    setCatalog(await adminCatalog());
                  }, "Varian disimpan.")}
                  className="rounded-full bg-[#1c1917] px-5 py-2 text-sm text-white disabled:opacity-50"
                >
                  Simpan varian
                </button>
              </Section>
              {catalog.families.map((family) => (
                <Section key={family.id} title={`${family.name} · ${family.gender}`}>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <button type="button" className="rounded-full border border-[#d9d0c6] px-4 py-1" onClick={() => setFamilyForm({ id: family.id, name: family.name, gender: family.gender, summary: family.summary || "" })}>
                      Edit di form
                    </button>
                    <button type="button" className="rounded-full border border-rose-200 px-4 py-1 text-rose-800" onClick={() => run(async () => { await deleteFamily(family.id); setCatalog(await adminCatalog()); }, "Keluarga dihapus.")}>
                      Hapus keluarga
                    </button>
                  </div>
                  <p className="text-sm text-[#6d655e]">{family.summary}</p>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {(family.variants || []).map((variant) => (
                      <article key={variant.id} className="overflow-hidden rounded-2xl border border-[#ece6de]">
                        {variant.image ? (
                          <img src={variantImage(variant)} alt="" className="aspect-[3/4] w-full object-cover object-top" />
                        ) : (
                          <div className="flex aspect-[3/4] items-center justify-center bg-[#efeae4] text-xs text-[#8a8178]">Belum ada gambar</div>
                        )}
                        <div className="space-y-2 p-3">
                          <div className="text-lg" style={{ fontFamily: serif }}>{variant.name}</div>
                          <p className="text-[11px] text-[#6d655e]">{variant.detail}</p>
                          <button
                            type="button"
                            className="text-xs underline"
                            onClick={() => setVariantForm({
                              familyId: family.id,
                              id: variant.id,
                              name: variant.name || "",
                              detail: variant.detail || "",
                              length: variant.length || "medium",
                              prompt: variant.prompt || "",
                            })}
                          >
                            Edit teks
                          </button>
                          <label className="block text-xs">
                            Ganti gambar
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="mt-1 block w-full text-xs"
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                event.target.value = "";
                                if (!file) return;
                                run(async () => {
                                  await uploadVariantImage({ familyId: family.id, variantId: variant.id, file });
                                  setCatalog(await adminCatalog());
                                }, "Gambar diganti.");
                              }}
                            />
                          </label>
                          <button type="button" className="text-xs text-rose-800" onClick={() => run(async () => { await deleteVariant(family.id, variant.id); setCatalog(await adminCatalog()); }, "Varian dihapus.")}>
                            Hapus
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                </Section>
              ))}
            </>
          )}

          {tab === "tampilan" && cfg && (
            <>
              <Section title="Teks situs">
                <label className="block text-sm">Nama
                  <input value={cfg.brand.name} onChange={(e) => updateConfig({ brand: { name: e.target.value } })} className={fieldClass()} />
                </label>
                <label className="block text-sm">Subjudul
                  <input value={cfg.brand.sub} onChange={(e) => updateConfig({ brand: { sub: e.target.value } })} className={fieldClass()} />
                </label>
                <label className="block text-sm">Judul beranda
                  <input value={cfg.brand.heroTitle} onChange={(e) => updateConfig({ brand: { heroTitle: e.target.value } })} className={fieldClass()} />
                </label>
                <label className="block text-sm">Teks beranda
                  <textarea value={cfg.brand.heroText} onChange={(e) => updateConfig({ brand: { heroText: e.target.value } })} className={fieldClass()} rows={3} />
                </label>
                <label className="block text-sm">Tentang
                  <textarea value={cfg.brand.about} onChange={(e) => updateConfig({ brand: { about: e.target.value } })} className={fieldClass()} rows={4} />
                </label>
                <label className="block text-sm">Catatan booking
                  <textarea value={cfg.brand.bookingNote} onChange={(e) => updateConfig({ brand: { bookingNote: e.target.value } })} className={fieldClass()} rows={3} />
                </label>
                <button type="button" disabled={busy} onClick={() => run(() => saveAdminSettings({ brand: cfg.brand }))} className="rounded-full bg-[#1c1917] px-5 py-2 text-sm text-white disabled:opacity-50">
                  Simpan tampilan
                </button>
              </Section>
              <Section title="Warna rambut">
                {(cfg.colors || []).map((color, index) => (
                  <div key={color.id || index} className="grid gap-2 sm:grid-cols-4">
                    <input value={color.name} onChange={(e) => {
                      const colors = cfg.colors.map((row, i) => i === index ? { ...row, name: e.target.value } : row);
                      updateConfig({ colors });
                    }} className={fieldClass()} />
                    <input value={color.hex} onChange={(e) => {
                      const colors = cfg.colors.map((row, i) => i === index ? { ...row, hex: e.target.value } : row);
                      updateConfig({ colors });
                    }} className={fieldClass()} />
                    <input value={color.prompt} onChange={(e) => {
                      const colors = cfg.colors.map((row, i) => i === index ? { ...row, prompt: e.target.value } : row);
                      updateConfig({ colors });
                    }} className={fieldClass()} />
                    <button type="button" className="text-sm text-rose-800" onClick={() => updateConfig({ colors: cfg.colors.filter((_, i) => i !== index) })}>Hapus</button>
                  </div>
                ))}
                <button type="button" className="rounded-full border border-[#d9d0c6] px-4 py-2 text-sm" onClick={() => updateConfig({ colors: [...(cfg.colors || []), { id: `color-${Date.now()}`, name: "Warna baru", hex: "#888888", prompt: "brown" }] })}>
                  Tambah warna
                </button>
                <button type="button" disabled={busy} onClick={() => run(() => saveAdminSettings({ colors: cfg.colors }))} className="rounded-full bg-[#1c1917] px-5 py-2 text-sm text-white disabled:opacity-50">
                  Simpan warna
                </button>
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
